import { tamanhoLegivel } from '../../formato';
import type { AnexoNaFila } from '../../sessoes/envios';
import { IconeArquivo, IconeFechar } from '../icones';
import { descrever } from './progresso-do-envio';

/**
 * O anexo na caixa do compositor.
 *
 * Três formas. Subindo, ele ganha uma barra e o botão vira CANCELAR: com
 * anexo de até 5 GiB o envio dura minutos, e uma fila sem saída prende a pessoa
 * a um arquivo escolhido por engano. Pronto ou com erro, volta à linha de
 * sempre — o nome, o tamanho ou o motivo, e o X que tira da fila.
 *
 * A barra é feita de `div`s e não de `<progress>`: o nativo desenha com as
 * cores do sistema, como os outros controles que a casa proíbe
 * (`web-controles.md`). O papel `progressbar` fica, que é o que leitor de tela
 * lê.
 */
export function ChipDeAnexo({
  anexo,
  aoTirar,
}: {
  anexo: AnexoNaFila;
  aoTirar: () => void;
}): React.JSX.Element {
  const subindo = !anexo.chave && !anexo.erro;

  const miniatura = anexo.previa ? (
    <img src={anexo.previa} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
  ) : (
    <span className="shrink-0 text-zinc-500">
      <IconeArquivo />
    </span>
  );

  if (!subindo) {
    return (
      <li
        className={
          // `max-w-full`: o motivo de uma recusa é uma frase, e sem teto
          // ela empurrava o anexo para fora da caixa.
          'flex max-w-full items-center gap-2 rounded-md border px-2 py-1 text-xs ' +
          (anexo.erro
            ? 'border-amber-800 bg-amber-950/30 text-amber-200'
            : 'border-zinc-700 bg-zinc-900 text-zinc-300')
        }
      >
        {miniatura}
        <span className="max-w-[16ch] truncate" title={anexo.nome}>
          {anexo.nome}
        </span>
        <span className={'min-w-0 ' + (anexo.erro ? 'break-words' : 'text-zinc-600')}>
          {anexo.erro ?? tamanhoLegivel(anexo.tamanho)}
        </span>
        <button
          onClick={aoTirar}
          aria-label={'Tirar ' + anexo.nome}
          className="flex items-center text-zinc-600 hover:text-zinc-200"
        >
          <IconeFechar tamanho={14} />
        </button>
      </li>
    );
  }

  const texto = anexo.progresso ? descrever(anexo.progresso) : null;
  const largura = texto?.percentual ?? 0;

  return (
    <li
      data-testid="anexo-em-envio"
      // Ocupa a linha: com barra e três números o chip de uma linha só não
      // cabe em 390 px. `min-w-0` deixa o nome truncar em vez de esticar.
      className="flex w-full min-w-0 max-w-full flex-col gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 sm:max-w-md"
    >
      <div className="flex items-center gap-2">
        {miniatura}
        <span className="min-w-0 flex-1 truncate" title={anexo.nome}>
          {anexo.nome}
        </span>
        <button
          onClick={aoTirar}
          aria-label={'Cancelar o envio de ' + anexo.nome}
          title="Cancelar o envio"
          // No dedo o alvo cresce para ~44 px. A margem negativa é SÓ na
          // horizontal, onde devolve o espaço que o preenchimento tomou: na
          // vertical ela fazia a área de toque invadir a barra logo abaixo, e
          // o dedo que errava o X por um pouco tocava em nada visível.
          className="-mx-1 flex shrink-0 items-center justify-center p-1 text-zinc-500 hover:text-zinc-200 pointer-coarse:-mx-3.5 pointer-coarse:p-3.5"
        >
          <IconeFechar tamanho={14} />
        </button>
      </div>

      <div
        role="progressbar"
        aria-label={'Envio de ' + anexo.nome}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(texto
          ? {
              'aria-valuenow': largura,
              'aria-valuetext': texto.finalizando
                ? 'finalizando'
                : `${texto.porcentagem}, ${texto.quantidade}`,
            }
          : { 'aria-valuetext': 'preparando' })}
        data-testid="barra-do-anexo"
        className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-800"
      >
        <div
          className={
            'h-full rounded-full bg-emerald-500 transition-[width] duration-300 ' +
            (!texto || texto.finalizando ? 'animate-pulse' : '')
          }
          style={{ width: largura + '%' }}
        />
      </div>

      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] tabular-nums text-zinc-500">
        {texto ? (
          texto.finalizando ? (
            <>
              <span>{texto.quantidade}</span>
              <span>finalizando…</span>
            </>
          ) : (
            <>
              <span className="text-zinc-300">{texto.porcentagem}</span>
              <span>{texto.quantidade}</span>
              {texto.ritmo ? <span>{texto.ritmo}</span> : null}
              {texto.restante ? <span>{texto.restante}</span> : null}
            </>
          )
        ) : (
          <span>preparando…</span>
        )}
      </p>
    </li>
  );
}
